import { useIntl } from 'react-intl';
import type { StepDefinition } from './Stepper';

interface Props {
  steps: StepDefinition[];
  current: number;
  selectable: (index: number) => boolean;
  onSelect: (index: number) => void;
}

export default function StepperTabs({ steps, current, selectable, onSelect }: Props) {
  const intl = useIntl();

  const moveBy = (offset: number) => {
    const next = current + offset;
    if (next >= 0 && next < steps.length && selectable(next)) onSelect(next);
  };

  return (
    <div
      role="tablist"
      aria-label={intl.formatMessage({ id: 'accounts.configForm.stepsLabel' })}
      className="flex gap-1 border-b border-slate-200/70"
    >
      {steps.map((step, index) => (
        <button
          key={step.id}
          id={`step-tab-${step.id}`}
          role="tab"
          type="button"
          aria-selected={index === current}
          aria-controls={`step-panel-${step.id}`}
          aria-disabled={!selectable(index)}
          tabIndex={index === current ? 0 : -1}
          onClick={() => (selectable(index) ? onSelect(index) : undefined)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') moveBy(1);
            if (event.key === 'ArrowLeft') moveBy(-1);
          }}
          className={tabClass(index === current, selectable(index))}
        >
          {intl.formatMessage({ id: step.labelId })}
        </button>
      ))}
    </div>
  );
}

function tabClass(active: boolean, selectable: boolean): string {
  const base =
    '-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand-200';
  if (active) return `${base} border-brand-600 text-brand-700`;
  if (!selectable) return `${base} border-transparent text-slate-300 cursor-not-allowed`;

  return `${base} border-transparent text-slate-500 hover:text-slate-700`;
}
